import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';

/**
 * Log or edit a CW time entry against a ticket.
 *
 * Props:
 *   ticketId — required
 *   existing — optional. When present, the modal becomes Edit mode and pre-fills
 *              from the existing CW time entry shape.
 *   onSuccess, onClose
 *
 * Both modes support explicit start date/time. When the explicit-start checkbox
 * is on, we send timeStart (and derive timeEnd from hours). When off, the
 * backend stamps "now + hours" as before.
 */
export default function AdminTimeEntryModal({ ticketId, existing = null, onClose, onSuccess }) {
  const currentUser = useAuthStore((s) => s.user);
  const cwMemberMapped = !!currentUser?.cwMemberId;
  const isEdit = !!existing;

  const [hours, setHours] = useState(
    existing?.hours != null ? String(existing.hours) : '0.25'
  );
  const [billable, setBillable] = useState(existing?.billableOption || 'Billable');
  const [workTypeId, setWorkTypeId] = useState(
    existing?.workType?.id ? String(existing.workType.id) : ''
  );
  const [workRoleId, setWorkRoleId] = useState(
    existing?.workRole?.id ? String(existing.workRole.id) : ''
  );
  const [notes, setNotes] = useState(existing?.notes || '');
  const [addTo, setAddTo] = useState('discussion');
  const [useExplicitStart, setUseExplicitStart] = useState(!!existing?.timeStart);
  const [startLocal, setStartLocal] = useState(
    existing?.timeStart ? toLocalInput(existing.timeStart) : todayLocalInput()
  );
  const [error, setError] = useState(null);

  const { data: workTypesData } = useQuery({
    queryKey: ['admin-lookup-work-types'],
    queryFn: () => adminLookupAPI.workTypes(),
    enabled: cwMemberMapped || isEdit,
    staleTime: 10 * 60 * 1000,
  });
  const { data: workRolesData } = useQuery({
    queryKey: ['admin-lookup-work-roles'],
    queryFn: () => adminLookupAPI.workRoles(),
    enabled: cwMemberMapped || isEdit,
    staleTime: 10 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (data) =>
      isEdit
        ? adminTicketsAPI.updateTimeEntry(ticketId, existing.id, data)
        : adminTicketsAPI.logTime(ticketId, data),
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onError: (err) =>
      setError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to save time entry'
      ),
  });

  const submit = () => {
    const payload = {
      hours: parseFloat(hours),
      billableOption: billable,
      workTypeId: workTypeId ? parseInt(workTypeId) : null,
      workRoleId: workRoleId ? parseInt(workRoleId) : null,
      notes: notes || undefined,
    };
    if (useExplicitStart) {
      const startIso = new Date(startLocal).toISOString();
      const endIso = new Date(
        new Date(startIso).getTime() + parseFloat(hours) * 3600 * 1000
      ).toISOString();
      payload.timeStart = startIso;
      payload.timeEnd = endIso;
    }
    // Note flags only apply on creation; CW does not let us toggle them after
    // the fact on a saved time entry.
    if (!isEdit) {
      payload.addToDiscussion = addTo === 'discussion';
      payload.addToInternal = addTo === 'internal';
      payload.addToResolution = addTo === 'resolution';
    }
    mutation.mutate(payload);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {isEdit ? 'Edit Time Entry' : 'Log Time'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {!isEdit && !cwMemberMapped ? (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-sm text-yellow-800 dark:text-yellow-200">
              Time entries require a ConnectWise member. Link this portal user to a CW member
              under Admin → Users → [your user].
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Hours">
                  <input
                    type="number"
                    step="0.25"
                    min="0.01"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                  />
                </Field>
                <Field label="Billable">
                  <Select
                    value={billable}
                    onChange={setBillable}
                    options={[
                      { value: 'Billable', label: 'Billable' },
                      { value: 'DoNotBill', label: 'Do Not Bill' },
                      { value: 'NoCharge', label: 'No Charge' },
                      { value: 'NoDefault', label: 'No Default' },
                    ]}
                  />
                </Field>
                <Field label="Work Type">
                  <Select
                    value={workTypeId}
                    onChange={setWorkTypeId}
                    options={(workTypesData?.data?.items || []).map((w) => ({
                      value: String(w.id),
                      label: w.name,
                    }))}
                    placeholder="Work Type…"
                  />
                </Field>
                <Field label="Work Role">
                  <Select
                    value={workRoleId}
                    onChange={setWorkRoleId}
                    options={(workRolesData?.data?.items || []).map((w) => ({
                      value: String(w.id),
                      label: w.name,
                    }))}
                    placeholder="Work Role…"
                  />
                </Field>
              </div>

              <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
                <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                  <input
                    type="checkbox"
                    checked={useExplicitStart}
                    onChange={(e) => setUseExplicitStart(e.target.checked)}
                  />
                  Set a specific start date / time (for back-dating)
                </label>
                {useExplicitStart && (
                  <input
                    type="datetime-local"
                    value={startLocal}
                    onChange={(e) => setStartLocal(e.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                  />
                )}
                {!useExplicitStart && (
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Otherwise stamped at the time you save.
                  </div>
                )}
              </div>

              <Field label="Notes">
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                />
              </Field>
              {!isEdit && (
                <Field label="Add note to">
                  <div className="flex gap-3">
                    {[
                      { value: 'discussion', label: 'Discussion' },
                      { value: 'internal', label: 'Internal' },
                      { value: 'resolution', label: 'Resolution' },
                    ].map((o) => (
                      <label key={o.value} className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name="addTo"
                          value={o.value}
                          checked={addTo === o.value}
                          onChange={() => setAddTo(o.value)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                </Field>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            disabled={(!isEdit && !cwMemberMapped) || mutation.isPending || !parseFloat(hours)}
            onClick={submit}
            className="px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Log Time'}
          </button>
        </div>
      </div>
    </div>
  );
}

function todayLocalInput() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
